import React from "react";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { SectionHeading, Stats } from "../DetailsPage/components";
import { ShowMoreSummary } from "../ShowMore/ShowMoreSummary";
import { StatsProps } from "../DetailsPage/components/Stats/Stats";
import { TriageBar, TriageBarProps } from "../TriageBar/TriageBar";
import { InventoryPageHeader } from "~/routes/page-layout";
import RemediationJumpToButton from "~/components/Remediation/RemediationJumToButton/RemediationJumpToButton";
import { ConversationProperties } from "~/hooks/useConversationStarter/types";
import {
  OpenExceptions,
  OpenExceptionsProps,
} from "~/components/app-search/OpenExceptions/OpenExceptions";
import { EnforcementState, Initiative } from "~/operations";
import { PreviewSummaryText } from "./PreviewSummaryText";

type DetailPageTopSectionProps = {
  content: {
    triageBar: TriageBarProps;
    variantTabs?: React.ReactNode;
    ticketsSummary?: React.ReactNode;
    summary?: string | null | undefined;
    stats: StatsProps["stats"];
    left?: {
      upper?: React.ReactNode;
      lower?: React.ReactNode;
    };
    right: React.ReactNode;
    conversation?: ConversationProperties;
    openExceptions?: OpenExceptionsProps;
    initiative?: Initiative;
    sectionHeading?: string;
    enforcementState?: {
      enforcementState?: EnforcementState;
      previewUntil?: string | null;
    };
  };
};

export function DetailPageTopSection({ content }: DetailPageTopSectionProps) {
  const showSummary = content.stats.length > 0 || content.summary;
  const inPreview =
    content.enforcementState?.enforcementState === EnforcementState.Preview;
  return (
    <>
      <TriageBar
        {...{
          title: content.triageBar.title,
          titleIcon: content.triageBar.titleIcon,
          fallbackPath: content.triageBar.fallbackPath,
          subheaders: content.triageBar.subheaders,
          toolbarItems: content.triageBar.toolbarItems,
          additionalMenuItems: content.triageBar.additionalMenuItems,
          isLoading: content.triageBar.isLoading,
          triage: content.triageBar.triage,
          showBackButton: content.triageBar.showBackButton,
          compact: content.triageBar.compact,
          availableRemediations: content.triageBar.availableRemediations,
          conversation: content.triageBar.conversation,
          initiativeStatus: content.initiative?.status,
        }}
      />
      {/* Note: we might need to show the InventoryPageHeader for the initiative
      detail page in the future when we have all information in the initiative
      payload */}
      {!content.initiative && (
        <InventoryPageHeader disableYPadding>
          <Grid size={{ xs: 12 }}>
            <Grid sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {content.openExceptions && (
                <OpenExceptions {...content.openExceptions} />
              )}
              {content.ticketsSummary && content.ticketsSummary}
            </Grid>
            <Box id="detail-content-description">
              {content.variantTabs && content.variantTabs}
              <SectionHeading
                heading={content.sectionHeading || "Description"}
              />

              {inPreview && (
                <PreviewSummaryText
                  previewUntil={content.enforcementState?.previewUntil}
                />
              )}

              <Grid
                container
                columnSpacing={3}
                rowSpacing={1}
                sx={{ justifyContent: "space-between" }}
              >
                <Grid
                  container
                  size={{ xs: 12, sm: 12, md: 8 }}
                  sx={{ height: "100%" }}
                >
                  {showSummary && (
                    <Grid size={12}>
                      <Typography
                        textTransform="uppercase"
                        fontWeight={700}
                        mb={1}
                      >
                        Summary
                      </Typography>
                      <Stats id="detail-content-stats" stats={content.stats} />
                      {content.summary && (
                        <ShowMoreSummary
                          id="detail-content-summary"
                          text={content.summary}
                        />
                      )}

                      {content.triageBar.availableRemediations
                        ?.hasRemediations && <RemediationJumpToButton />}
                    </Grid>
                  )}
                  <Grid
                    container
                    size={12}
                    spacing={3}
                    className="detail-content-left"
                  >
                    {content.left?.upper && content.left.upper}
                    <Grid
                      size={{ xs: 12, md: 6 }}
                      className="detail-content-left-lower"
                    >
                      {content.left?.lower && content.left.lower}
                    </Grid>
                  </Grid>
                </Grid>
                <Grid
                  className="detail-content-right"
                  size={{ xs: 12, sm: "auto", md: 4 }}
                >
                  {content.right}
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </InventoryPageHeader>
      )}
    </>
  );
}
